import React, { useCallback, useEffect, useRef, useSyncExternalStore } from 'react';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import RecentEventsSkeleton from './RecentEventsSkeleton';
import NoData from './NoData';
import SessionPreview from './SessionPreview';
import useMediaContent from '../useMediaContent';
import ErrorState from './ErrorState';
import ContentDialog from '../uiComponents/ContentDialog/ContentDialog';
import SessionsListModal from '../SessionsListModal/SessionsListModal';
import UploadFileButton from './UploadFileButton';
import SavedSearchInput from './AiClips/SavedSearchInput';
import { currentUser } from '@/stores/user';
import EventBus from '@/libs/eventBus/eventBus';
import { CustomEvents } from '@/libs/eventBus/constants';
import Button from '@/components/atoms/Button/Button';
import { useAppContext } from '@/context/AppContext/AppContext';
import useDialog from '@/components/organisms/useDialog';
import { usePusherContext } from '@/context/PusherContext/PusherContext';
import useContentUpload from '@/hooks/useContentUpload';
import { ContentStatesEnum } from '@/Pages/VideoImportPage/constants';
import { FeatureFlagKeys } from '@/services/featureFlag';
import { isSimplifiedUploadEnabled } from '@/stores/clip';
import featureFlagStore from '@/stores/featureFlagStore';
import { OPEN_NEW_CONTENT_MODAL_EVENT } from '@/components/organisms/NewContentModal/constants';
import { UPLOAD_EDITOR_READY_EVENT } from '@/components/organisms/ContentReadinessPopup/constants';
import { classnames } from '@/libs/utils';
import IconButton from '@/components/atoms/Button/IconButton';

export default function RecentEvents() {
  const { mediaContent, showErrorState, fetchMediaContentList } = useMediaContent();
  const { isPusherConnected } = usePusherContext();
  const { adminAppStore } = useAppContext();
  const { changeContentUploadStatus, contentUploadList } = useContentUpload();
  const timeoutRef = useRef<NodeJS.Timeout>();
  const idsRef = useRef<string[]>([]);
  const navigate = useNavigate();
  const featureFlags = useSyncExternalStore(featureFlagStore.subscribe, featureFlagStore.getSnapshot);
  const isCLHomePageEnabled = featureFlags[FeatureFlagKeys.Use_CL_New_HomePage];

  const { isFetching: loading } = useQuery({
    queryKey: 'recent-events',
    queryFn: () => loadMediaContent()
  });

  const {
    isOpen: isSessionsListDialogOpen,
    openDialog: openSessionsListDialog,
    closeDialog: closeSessionsListDialog
  } = useDialog();

  const onClickShowAllButton = useCallback(() => {
    if (featureFlags[FeatureFlagKeys.Use_CL_Uploads_In_VL]) {
      adminAppStore.dispatch?.('contentStudio/toggleVideoLibraryDialog', true);
    } else {
      openSessionsListDialog();
    }
  }, [adminAppStore, featureFlags, openSessionsListDialog]);

  function loadMediaContent(idsArray?: string[]) {
    return fetchMediaContentList({
      organization: currentUser.getSnapshot()?.organization as string,
      limit: isCLHomePageEnabled ? 6 : 5,
      ...(idsArray && { idsArray })
    });
  }

  const openSessionsDialog = useCallback(() => {
    // Open the shell-level New Upload modal only when the simplified upload FF is on;
    // otherwise fall back to the legacy import page navigation.
    if (isSimplifiedUploadEnabled()) {
      document.dispatchEvent(new CustomEvent(OPEN_NEW_CONTENT_MODAL_EVENT));
    } else {
      navigate('/import');
    }
  }, [navigate]);

  useEffect(() => {
    const eventListener = EventBus.on(CustomEvents.UploadContentDone, loadMediaContent);

    return () => {
      EventBus.off(CustomEvents.UploadContentDone, eventListener);
    };
  }, []);

  useEffect(() => {
    const eventListener = EventBus.on(CustomEvents.ReloadCLHomepageMediaContent, loadMediaContent);

    return () => {
      EventBus.off(CustomEvents.ReloadCLHomepageMediaContent, eventListener);
    };
  }, []);

  // When upload_editor_status flips to DONE (via the org-channel WS), patch just that card in
  // the list so the thumbnail and badge update in sync with the readiness popup, without waiting
  // for the next full list refetch.
  useEffect(() => {
    if (!isSimplifiedUploadEnabled()) {
      return;
    }
    const handler = (event: Event) => {
      const detail = (event as CustomEvent).detail as { content_id?: string; upload_editor_status?: string };
      if (detail?.upload_editor_status === 'DONE' && detail.content_id) {
        loadMediaContent([detail.content_id]);
      }
    };
    document.addEventListener(UPLOAD_EDITOR_READY_EVENT, handler);
    return () => document.removeEventListener(UPLOAD_EDITOR_READY_EVENT, handler);
  }, []);

  const loadNewContent = () => {
    clearTimeout(timeoutRef.current);
    timeoutRef.current = undefined;

    loadMediaContent([...idsRef.current]);

    idsRef.current = [];
  };

  const handleContentStatusUpdate = (data, type: ContentStatesEnum) => {
    if (mediaContent.some(content => content.id === data.upload_id) && !idsRef.current.includes(data.upload_id)) {
      idsRef.current.push(data.upload_id);
    }
    if (contentUploadList[data.upload_id]) {
      changeContentUploadStatus(data, type, data.upload_id);
    }
    if (!timeoutRef.current && idsRef.current.length > 0) {
      timeoutRef.current = setTimeout(() => {
        loadNewContent();
      }, 5000);
    }
  };

  const pollForUpdatedState = () => {
    if (mediaContent.find(c => c.media_source_type === 'UPLOAD' && c.batch_status === 'PROCESSING')) {
      return setTimeout(() => {
        loadMediaContent();
      }, 30000);
    }
  };

  useEffect(() => {
    let contentProcessingListener;
    let clipGenerationListener;
    let pollTimeoutId;
    if (isPusherConnected) {
      contentProcessingListener = EventBus.on(CustomEvents.TranscriptionStatusUpdated, data =>
        handleContentStatusUpdate(data, ContentStatesEnum.Processing)
      );
      clipGenerationListener = EventBus.on(CustomEvents.ClipGenerationStatusUpdated, data =>
        handleContentStatusUpdate(data, ContentStatesEnum.GeneratingCompleted)
      );
    } else {
      pollTimeoutId = pollForUpdatedState();
    }

    return () => {
      if (pollTimeoutId) {
        clearTimeout(pollTimeoutId);
      }
      if (contentProcessingListener) {
        EventBus.off(CustomEvents.TranscriptionStatusUpdated, contentProcessingListener);
      }
      if (clipGenerationListener) {
        EventBus.off(CustomEvents.TranscriptionStatusUpdated, clipGenerationListener);
      }
    };
  }, [mediaContent, isPusherConnected]);

  const goToEventsPage = useCallback(() => {
    window.location.replace('/events');
  }, []);

  const noData = !loading && mediaContent.length === 0;

  const {
    adminAppStore: { user = { is_content_lab_standalone: false } }
  } = useAppContext();
  const is_content_lab_standalone = user?.is_content_lab_standalone ?? false;

  return (
    <div className={classnames(isCLHomePageEnabled ? 'my-10 rounded-xl bg-white' : 'mb-8')}>
      {!isCLHomePageEnabled && (
        <div className="mb-3 flex w-full items-center justify-between pl-7 pr-4 pt-6">
          <div className="hidden text-2xl font-medium leading-tight tracking-tighter text-black lg:block">
            {is_content_lab_standalone ? "Let's unleash your content!" : 'All Recordings'}
          </div>
          <SavedSearchInput />

          <div>
            <Button variation="list" onClick={onClickShowAllButton} disabled={noData} trackingId="show-all-button">
              Show all
            </Button>
          </div>
        </div>
      )}

      {isCLHomePageEnabled && (
        <div className="mb-3 flex w-full items-center justify-between pr-4">
          <div>
            <div className="mb-2 font-medium text-slate-950">Recently Added</div>
            <p className="text-sm text-slate-600">Quick access to the newest videos in your library.</p>
          </div>
          <div className="flex items-center gap-4">
            <IconButton
              icon="IconSearch"
              size="small"
              content="Video Library"
              trackingId="video-library-cta"
              onClick={onClickShowAllButton}
            />
            <IconButton
              icon="IconUpload"
              size="small"
              variation="filled"
              content="Upload"
              trackingId="import-file-button"
              onClick={openSessionsDialog}
            />
          </div>
        </div>
      )}

      <div
        className={classnames(
          'no-scrollbar flex w-full overflow-y-scroll',
          !isCLHomePageEnabled ? 'space-x-4 px-7 pb-5 pr-4 pt-2' : 'gap-6 py-2'
        )}
      >
        {loading ? (
          <RecentEventsSkeleton />
        ) : showErrorState ? (
          <ErrorState />
        ) : noData ? (
          <NoData>
            <button
              className="mt-3 rounded-lg border border-slate-200 px-3 py-2 text-xs font-medium"
              onClick={is_content_lab_standalone ? openSessionsDialog : goToEventsPage}
            >
              {is_content_lab_standalone ? 'Upload recording' : 'Go to events'}
            </button>
          </NoData>
        ) : (
          <React.Fragment>
            {!isCLHomePageEnabled && <UploadFileButton openSessionsDialog={openSessionsDialog} />}
            {mediaContent.map(session => {
              return <SessionPreview session={session} key={session.id} onDeleteSession={loadMediaContent} />;
            })}
          </React.Fragment>
        )}
      </div>
      <ContentDialog
        isOpen={isSessionsListDialogOpen}
        setIsOpen={closeSessionsListDialog}
        title="All Recordings"
        size="xlarge"
      >
        <SessionsListModal />
      </ContentDialog>
    </div>
  );
}
